import dayjs from 'dayjs';

import type {CourseExpandedCommon} from '../../../api/course';
import type {CoreCourseApiModel} from '../../../api/types/core/models';
import type {BookingPeriod} from '../types';

import {dateTimeObjToTimestamp} from '../../../utils/datetime';
import {envManager} from '../../../utils/env';
import {logger} from '../../../utils/logger';

export function findCurrentPeriodForRestartingCourse(
  course: CourseExpandedCommon,
): BookingPeriod {
  if (envManager.isB2b) {
    /**
     * It finds the current period, given that the course restart_dates
     * is an array of DateTimeObj ordered from the latest to the oldest,
     * but it can contain dates in the future
     */
    const restartDates = (course.restarted_dates || []).slice();

    if (course.start_date) {
      restartDates.push(course.start_date);
    }

    if (course.end_date) {
      restartDates.unshift(course.end_date);
    }

    for (let i = 0; i < restartDates.length; i++) {
      const next = restartDates[i];
      const current = restartDates[i + 1];

      if (current && next) {
        const now = new Date();
        const nextTimestamp = dateTimeObjToTimestamp(next);
        const currentTimestamp = dateTimeObjToTimestamp(current);

        if (
          now > new Date(currentTimestamp) &&
          now <= new Date(nextTimestamp)
        ) {
          return {
            end: nextTimestamp,
            start: currentTimestamp,
          };
        }
      }
    }

    // If we didn't find the period, it means that all restarted dates is before now (or something unexpected)
    // It's incorrect case, but we should return something to prevent a fall of the application
    logger.error(`Could not find a period for course with id ${course._id}`);

    const lastRestartedDate = restartDates[restartDates.length - 1];
    const start = lastRestartedDate
      ? dateTimeObjToTimestamp(lastRestartedDate)
      : new Date().valueOf();
    const end = dayjs(start)
      .add(course.edition?.duration ?? 30, 'day')
      .toDate()
      .valueOf();

    return {end, start};
  } else {
    /**
     * for evergreen programs we give clients ability
     * to book N conferences each period (month-to-month)
     */
    const lastRestartedDate = (course as CoreCourseApiModel).restarted_on
      .slice()
      .pop();

    const nextPlannedRestartDate = course.restart_date;

    return {
      end: dateTimeObjToTimestamp(nextPlannedRestartDate || course.end_date!),
      start: dateTimeObjToTimestamp(lastRestartedDate || course.start_date!),
    };
  }
}
